import { Navigate } from "react-router-dom";
import { Button } from "antd";
import { BlobLoader } from "../ui/loader/BlobLoader";
import { useSession } from "../hooks/useSession";
import { isAvailable } from "../../utils/isAvailable";
import { SECCION_PERMISOS } from "../../assets/permisos-template";

// Each signed-in account belongs to exactly one area of the app, and each
// area has a home page.
const AREA_HOME = {
  admin: "/",
  instructor: "/instructor",
  student: "/student",
};

/** The area a session belongs to. */
export const areaOf = (session) => {
  if (session?.kind === "STUDENT") return "student";
  if (session?.user?.type?.toUpperCase() === "INSTRUCTOR") return "instructor";
  return "admin";
};

/** Where a session lands after login, or when it wanders into another area. */
export const homeFor = (session) => AREA_HOME[areaOf(session)];

/**
 * The one route guard. PrivateRouter, StudentRouter and InstructorRouter are
 * thin wrappers over it, so the routing rules live in one place.
 *
 *   area     "admin" | "instructor" | "student" — who may enter
 *   seccion  admin only: a permission section; entering needs its `modulo`
 *
 * Someone in the wrong area is sent to their own home, never logged out. The
 * redirect is always to the home of the area they DO belong to, which admits
 * them by construction — so the guard cannot bounce between pages.
 */
export const SessionGuard = ({ children, area, seccion }) => {
  const session = useSession();

  if (session.status === "loading") {
    return (
      <div className="suspense-wrapper">
        <BlobLoader />
      </div>
    );
  }

  // The API could not be reached. The session may be perfectly valid, so it
  // is kept; the user can retry instead of being signed out.
  if (session.status === "error") {
    return (
      <div className="suspense-wrapper columna" style={{ gap: 12 }}>
        <span>No se pudo conectar con el servidor.</span>
        <Button onClick={() => window.location.reload()}>Reintentar</Button>
      </div>
    );
  }

  if (session.status !== "authenticated") {
    return <Navigate to="/login" replace />;
  }

  const home = homeFor(session);

  if (area && areaOf(session) !== area) {
    return <Navigate to={home} replace />;
  }

  // A section the role cannot open sends them to their home page, which has
  // no section requirement — so this cannot loop either.
  if (
    seccion &&
    !isAvailable(seccion, SECCION_PERMISOS.modulo, session.user?.rol?.permissions)
  ) {
    return <Navigate to={home} replace />;
  }

  return children;
};
